import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

/**
 * AuthStateManager - Ensures UI state stays in sync with auth state
 * Handles redirects for protected routes
 */
export function AuthStateManager({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle protected routes
  useEffect(() => {
    if (loading) return;

    const protectedRoutes = ['/profile', '/settings', '/create-prompt'];
    const currentPath = location.pathname;
    
    // Only redirect if user is not logged in and on a protected route
    if (!user && protectedRoutes.some(route => currentPath.startsWith(route))) {
      console.log('[AUTH STATE] Redirecting unauthenticated user from protected route:', currentPath);
      navigate('/auth', { replace: true, state: { from: currentPath } });
    }
  }, [user, loading, location.pathname, navigate]);

  // Show loading state during initial auth check
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}